import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/cognivault_logo.png';
import { cleanApiBaseUrl } from '../lib/api';

const SESSION_KEY = 'cv_demo_access';

export function hasDemoAccess() {
  return sessionStorage.getItem(SESSION_KEY) === 'granted';
}

export default function DemoGate() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validCode, setValidCode] = useState(null);

  React.useEffect(() => {
    // We could use a dedicated validate endpoint, but for now we'll fetch the current
    // expected code from the config (which reads from env on the server).
    const apiBase = cleanApiBaseUrl();
    fetch(`${apiBase}/api/config`)
      .then(r => r.json())
      .then(data => { if (data.demo_code) setValidCode(data.demo_code); })
      .catch(() => {});
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setTimeout(() => {
      if (code.trim() === (validCode || 'CogniVault2026')) {
        sessionStorage.setItem(SESSION_KEY, 'granted');
        navigate('/login');
      } else {
        setError('Invalid access code. Please contact CogniAI Inc. for access.');
        setLoading(false);
      }
    }, 600);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0A0F1E 0%, #0F1F3D 50%, #0A0F1E 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '2rem', fontFamily: 'sans-serif',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background grid */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-block',
            background: 'white', borderRadius: '16px',
            padding: '16px 24px', marginBottom: '20px',
            boxShadow: '0 0 60px rgba(37,99,235,0.2)',
          }}>
            <img src={logo} alt="CogniVault" style={{ height: '48px', width: 'auto', objectFit: 'contain', display: 'block' }} />
          </div>
          <p style={{ color: 'rgba(232,237,245,0.5)', fontSize: '14px', margin: 0 }}>
            Live demo environment
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px', padding: '40px',
          backdropFilter: 'blur(12px)',
        }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: '500', color: '#F0F4FF', marginBottom: '8px', marginTop: 0 }}>
            Enter access code
          </h1>
          <p style={{ color: 'rgba(232,237,245,0.5)', fontSize: '14px', marginBottom: '32px', lineHeight: '1.6' }}>
            This demo environment is access-controlled. Enter the code provided by CogniAI Inc. to continue.
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block', fontSize: '12px',
                color: 'rgba(232,237,245,0.5)',
                marginBottom: '8px', letterSpacing: '0.05em', textTransform: 'uppercase',
              }}>
                Access code
              </label>
              <input
                type="password"
                value={code}
                onChange={e => { setCode(e.target.value); setError(''); }}
                placeholder="Enter your access code"
                required
                autoFocus
                style={{
                  width: '100%', padding: '12px 16px',
                  background: 'rgba(255,255,255,0.06)',
                  border: error ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px', color: '#E8EDF5',
                  fontSize: '15px', outline: 'none',
                  boxSizing: 'border-box', letterSpacing: '0.1em',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => { if (!error) e.target.style.borderColor = 'rgba(37,99,235,0.6)'; }}
                onBlur={e => { if (!error) e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
              />
              {error && (
                <p style={{ color: '#F87171', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !code}
              style={{
                width: '100%', padding: '13px',
                background: loading || !code ? 'rgba(37,99,235,0.4)' : '#2563EB',
                color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '15px',
                fontWeight: '500', cursor: loading || !code ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}>
              {loading ? 'Verifying...' : 'Access demo →'}
            </button>
          </form>
        </div>

        {/* Footer note */}
        <p style={{
          textAlign: 'center', marginTop: '24px',
          fontSize: '13px', color: 'rgba(232,237,245,0.3)',
          lineHeight: '1.6',
        }}>
          Don't have an access code?{' '}
          <a href="/#contact" style={{ color: '#60A5FA', textDecoration: 'none' }}>
            Contact CogniAI Inc.
          </a>
        </p>
      </div>
    </div>
  );
}
